import { FunctionComponent, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useAtomValue } from 'jotai'
import { uniqBy } from 'lodash'
import { DeleteValidationModal, IconButton, Input, RoundedButton } from '../../shared'
import {
  useConversations,
  useDeleteConversation,
  useRenameConversation,
} from '../../../hooks/queries/statsAssistant'
import { useDebouncedFilters } from '../../../hooks/utils/useDebouncedFilters.hook'
import { statsAssistantProcessingUlidsAtom } from '../../../state/statsAssistant'
import {
  formatConversationDate,
  groupConversationsByDate,
  plainTextPreview,
} from '../utils/conversationHistory.utils'
import { ConversationHistoryListProps } from './ConversationHistoryList.model'
import modalStyles from '../../shared/modal/Modal/Modal.module.scss'
import styles from './ConversationHistoryList.module.scss'

export const ConversationHistoryList: FunctionComponent<ConversationHistoryListProps> = ({
  activeUlid,
  onSelect,
  onDeleted,
}) => {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [renamingUlid, setRenamingUlid] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deleteUlid, setDeleteUlid] = useState<string | null>(null)

  useDebouncedFilters(searchInput, setSearch)

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useConversations({
    search: search || undefined,
  })
  const processingUlids = useAtomValue(statsAssistantProcessingUlidsAtom)
  const renameMutation = useRenameConversation()
  const deleteMutation = useDeleteConversation()

  // Une conversation ajoutée en tête de liste côté client peut réapparaître dans la page suivante.
  const conversations = useMemo(
    () => uniqBy(data?.pages.flatMap((page) => page.items) ?? [], 'ulid'),
    [data],
  )
  const groups = useMemo(() => groupConversationsByDate(conversations), [conversations])

  const startRename = useCallback((ulid: string, currentTitle: string | null) => {
    setRenamingUlid(ulid)
    setRenameValue(currentTitle ?? '')
  }, [])

  const commitRename = useCallback(async () => {
    if (!renamingUlid) return
    const trimmed = renameValue.trim()
    if (trimmed) {
      await renameMutation.mutateAsync({ ulid: renamingUlid, title: trimmed })
    }
    setRenamingUlid(null)
  }, [renameMutation, renameValue, renamingUlid])

  // Input n'expose que onKeyPress, qui ne reçoit pas Échap.
  const handleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') void commitRename()
      if (event.key === 'Escape') setRenamingUlid(null)
    },
    [commitRename],
  )

  const confirmDelete = useCallback(async () => {
    if (!deleteUlid) return
    await deleteMutation.mutateAsync(deleteUlid)
    onDeleted(deleteUlid)
    setDeleteUlid(null)
  }, [deleteMutation, deleteUlid, onDeleted])

  return (
    <>
      <div className={styles.search}>
        <Input
          type="search"
          name="stats_assistant_history_search"
          icon="search"
          height="small"
          colorPreset="light"
          placeholder="Rechercher…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          testId="input-stats_assistant-history-search"
        />
      </div>

      <div className={styles.list}>
        {isLoading && <div className={styles.empty}>Chargement…</div>}
        {!isLoading && conversations.length === 0 && (
          <div className={styles.empty}>Aucune conversation</div>
        )}
        {groups.map((group) => (
          <section key={group.key} className={styles.group}>
            <h3 className={styles.groupLabel}>{group.label}</h3>
            {group.items.map((item) => {
              const preview = item.lastMessagePreview
                ? plainTextPreview(item.lastMessagePreview)
                : ''

              return (
                <div
                  key={item.ulid}
                  className={classNames(styles.item, { [styles.active]: item.ulid === activeUlid })}
                >
                  {renamingUlid === item.ulid ? (
                    <div className={styles.renameInput} onKeyDown={handleRenameKeyDown}>
                      <Input
                        name="stats_assistant_history_rename"
                        height="small"
                        colorPreset="light"
                        customClassName={styles.field}
                        value={renameValue}
                        autofocus
                        onChange={(event) => setRenameValue(event.target.value)}
                        onBlur={() => void commitRename()}
                        testId="input-stats_assistant-history-rename"
                      />
                    </div>
                  ) : (
                    <>
                      {/* Ligne cliquable (titre, aperçu, badge) : pas d'équivalent dans les boutons partagés. */}
                      <button
                        type="button"
                        className={styles.itemMain}
                        onClick={() => onSelect(item.ulid)}
                        data-test-id={`button-stats_assistant-history-select-${item.ulid}`}
                      >
                        <span className={styles.itemTitle}>
                          <span className={styles.itemTitleText}>
                            {item.title ?? (preview || 'Sans titre')}
                          </span>
                          {(item.isProcessing || processingUlids.includes(item.ulid)) && (
                            <span className={styles.processingBadge} aria-label="Analyse en cours">
                              …
                            </span>
                          )}
                          <span className={styles.itemDate}>
                            {formatConversationDate(item.updatedAt)}
                          </span>
                        </span>
                        {preview && <span className={styles.itemPreview}>{preview}</span>}
                      </button>
                      <div className={styles.itemActions}>
                        <IconButton
                          icon="pencil"
                          theme="transparent-dark"
                          size="nano"
                          noBorder
                          title="Renommer"
                          onClick={() => startRename(item.ulid, item.title)}
                          testId={`button-stats_assistant-history-rename-${item.ulid}`}
                        />
                        <IconButton
                          icon="trash"
                          theme="transparent-dark"
                          size="nano"
                          noBorder
                          title="Supprimer"
                          onClick={() => setDeleteUlid(item.ulid)}
                          testId={`button-stats_assistant-history-delete-${item.ulid}`}
                        />
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </section>
        ))}
        {hasNextPage && (
          <RoundedButton
            label={isFetchingNextPage ? 'Chargement…' : 'Afficher plus'}
            theme="transparent-dark"
            size="micro"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
            testId="button-stats_assistant-history-load_more"
          />
        )}
      </div>

      <DeleteValidationModal
        display={deleteUlid !== null}
        title="Supprimer cette conversation ?"
        confirmLabel="Supprimer"
        testId="modal-stats_assistant-conversation-delete"
        veilClassName={modalStyles.backgroundVeilTopmost}
        onClose={() => setDeleteUlid(null)}
        onSubmit={confirmDelete}
      />
    </>
  )
}
